import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Send, MessageSquare, Loader2, ShieldCheck, Bell } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function ChatThread({ conn, meId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/connections/${conn.id}/messages`).then((r) => setMessages(r.data.messages)).catch(() => {});
  }, [conn.id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/connections/${conn.id}/messages`, { text });
      setText("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="card-surface hairline-gold flex flex-col" data-testid="chat-thread">
      <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-slate-100" data-testid="chat-peer-name">{conn.peer?.name} · {conn.peer?.company}</p>
          <p className="text-[10px] font-mono text-emerald-400 uppercase tracking-widest flex items-center gap-1.5 mt-0.5"><ShieldCheck size={11} /> Secure member conversation</p>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300" data-testid="chat-close"><X size={16} /></button>
      </div>
      <div className="h-80 overflow-y-auto px-5 py-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && <p className="text-center text-xs text-slate-500 py-10" data-testid="chat-empty">Start the conversation — you're connected.</p>}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.from_user_id === meId ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-sm px-3.5 py-2.5 text-[13px] leading-relaxed ${m.from_user_id === meId ? "bg-gold/15 border border-gold/30 text-slate-100" : "bg-navy-800 border border-slate-700 text-slate-300"}`}
              data-testid={`chat-msg-${i}`}>
              <p>{m.text}</p>
              <p className="text-[9px] font-mono text-slate-500 mt-1 text-right">{String(m.ts).slice(11, 16)}</p>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="border-t border-slate-800 p-3 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" maxLength={2000}
          data-testid="chat-input" className="input-dark text-sm flex-1" />
        <button type="submit" disabled={sending || !text.trim()} data-testid="chat-send-btn" className="btn-gold !px-4 !py-2.5 text-xs">
          {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
        </button>
      </form>
    </div>
  );
}

export default function Connections() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("requests");
  const [activeChat, setActiveChat] = useState(null);
  const [unread, setUnread] = useState(0);
  const [working, setWorking] = useState("");

  const load = useCallback(() => {
    api.get("/connections").then((r) => setData(r.data)).catch((err) => { if (err.response?.status === 401) navigate("/login"); });
    api.get("/my/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
  }, [navigate]);
  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const act = async (id, action) => {
    setWorking(id + action);
    try {
      await api.post(`/connections/${id}/${action}`);
      toast.success(action === "accept" ? "Request accepted — you can now chat" : "Request declined");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setWorking(""); }
  };

  if (!data) return <div className="max-w-5xl mx-auto px-8 py-24 text-sm font-mono text-slate-500 animate-pulse" data-testid="connections-loading">Loading your network…</div>;

  const tabs = [["requests", `Requests (${data.received.length})`], ["sent", `Sent (${data.sent.length})`], ["connections", `Connections (${data.connections.length})`]];

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-12" data-testid="connections-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="gold-kicker mb-2">My Network</p>
          <h1 className="font-display text-3xl font-bold text-slate-100" data-testid="connections-headline">CONNECTIONS & MESSAGES</h1>
        </div>
        <div className="flex items-center gap-3">
          {unread > 0 && <span className="flex items-center gap-1.5 text-[10px] font-mono text-gold border border-gold/40 px-3 py-1.5 rounded-sm" data-testid="unread-badge"><Bell size={11} /> {unread} unread</span>}
          <Link to="/members" className="btn-gold text-xs" data-testid="find-members-btn">FIND MEMBERS</Link>
        </div>
      </div>

      <div className="flex gap-1.5 mb-6" data-testid="connections-tabs">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`text-[11px] font-mono uppercase tracking-wider px-4 py-2.5 rounded-sm border transition-colors ${tab === k ? "border-gold text-gold bg-gold/10" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "requests" && (
        <div className="space-y-3" data-testid="requests-list">
          {data.received.length === 0 && <div className="card-surface p-10 text-center text-sm text-slate-500" data-testid="requests-empty">No pending requests. Members can find you in the directory.</div>}
          {data.received.map((c) => (
            <div key={c.id} className="card-surface p-5" data-testid={`request-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-100">{c.peer?.name} <span className="text-gold font-normal">· {c.peer?.company}</span></p>
                  <p className="text-[10px] font-mono text-slate-500 mt-0.5">{c.peer?.industry} {c.peer?.location && `· ${c.peer.location}`} · {String(c.ts).slice(0, 10)}</p>
                  <div className="mt-3 border-l-2 border-gold/50 pl-3">
                    <p className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mb-1">Short Note</p>
                    <p className="text-[13px] text-slate-300 leading-relaxed" data-testid={`request-note-${c.id}`}>{c.note}</p>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => act(c.id, "accept")} disabled={!!working} data-testid={`accept-${c.id}`} className="btn-gold !px-4 !py-2.5 text-[11px]">
                    {working === c.id + "accept" ? <Loader2 size={12} className="animate-spin" /> : <><Check size={12} /> ACCEPT</>}
                  </button>
                  <button onClick={() => act(c.id, "decline")} disabled={!!working} data-testid={`decline-${c.id}`} className="btn-outline !px-4 !py-2.5 text-[11px] !border-red-500/40 !text-red-400"><X size={12} /> DECLINE</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "sent" && (
        <div className="space-y-3" data-testid="sent-list">
          {data.sent.length === 0 && <div className="card-surface p-10 text-center text-sm text-slate-500" data-testid="sent-empty">No pending requests — find members and send a request.</div>}
          {data.sent.map((c) => (
            <div key={c.id} className="card-surface p-5 flex items-center justify-between gap-4" data-testid={`sent-${c.id}`}>
              <div>
                <p className="text-sm font-bold text-slate-100">{c.peer?.name} <span className="text-gold font-normal">· {c.peer?.company}</span></p>
                <p className="text-[11px] text-slate-500 mt-1 italic">"{c.note.slice(0, 120)}{c.note.length > 120 ? "…" : ""}"</p>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-amber-400 border border-amber-500/40 px-2.5 py-1 rounded-sm shrink-0">Awaiting acceptance</span>
            </div>
          ))}
        </div>
      )}

      {tab === "connections" && !activeChat && (
        <div className="space-y-3" data-testid="connections-list">
          {data.connections.length === 0 && <div className="card-surface p-10 text-center text-sm text-slate-500" data-testid="connections-empty">No connections yet — accepted requests appear here.</div>}
          {data.connections.map((c) => (
            <button key={c.id} onClick={() => setActiveChat(c)} className="card-surface p-5 w-full text-left flex items-center justify-between gap-4 hover:border-gold/40 transition-colors" data-testid={`connection-${c.id}`}>
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-11 h-11 rounded-sm bg-navy-700 border border-slate-700 flex items-center justify-center font-display font-bold text-gold shrink-0">{c.peer?.name?.charAt(0)}</div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-100 truncate">{c.peer?.name} <span className="text-gold font-normal">· {c.peer?.company}</span></p>
                  <p className="text-[10px] font-mono text-slate-500 mt-0.5">Connected {String(c.accepted_at || "").slice(0, 10)} · {c.message_count} messages</p>
                </div>
              </div>
              <MessageSquare size={16} className="text-gold shrink-0" />
            </button>
          ))}
        </div>
      )}

      {tab === "connections" && activeChat && (
        <ChatThread conn={activeChat} meId={String(user?._id || user?.id)} onClose={() => { setActiveChat(null); load(); }} />
      )}
    </div>
  );
}
